import {
	CalendarCheck,
	ChevronDown,
	ChevronUp,
	CircleUser,
	ExternalLink,
	FileSignature,
	Link,
	Loader2,
	LogOut,
	Settings,
	Users,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { safeOpen } from "@/lib/safeOpen";
import { CampaignsIcon } from "../../icons/campaigns-icon";
import { NAV_STROKE_LOCAL, NAV_STROKE_LUCIDE } from "../../icons/stroke";
import { SubPartnersIcon } from "../../icons/sub-partners-icon";
import { SupportIcon } from "../../icons/support-icon";
import { CloseButton } from "../CloseButton";
import { type NavbarProps, showNavItem } from "../Navbar/types";

interface MobileFlexDrawerProps
	extends Omit<NavbarProps, "isCollapsed" | "onToggleCollapse"> {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onNavigate: (href: string, internal?: boolean) => void;
	/** Current pathname used to determine active state */
	pathname: string;
}

/**
 * One tappable row of the sheet — Figma `Row · …`: 12px of air above and below
 * a 24px line, so 48px tall, with 24px side padding and a 22px icon 16px from
 * the 16px SemiBold label. The icon carries its own grey (#4A5565); only the
 * active and danger states tint icon and label alike. The menu's first row
 * passes `className="pt-4"` for the 52px Figma draws there.
 */
function SheetRow({
	icon: Icon,
	iconClassName,
	className,
	stroke = NAV_STROKE_LUCIDE,
	title,
	isActive,
	danger,
	disabled,
	trailing,
	onClick,
}: {
	icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
	iconClassName?: string;
	className?: string;
	/** See {@link NAV_STROKE_LUCIDE} — depends on the icon's viewBox grid. */
	stroke?: number;
	title: string;
	isActive?: boolean;
	danger?: boolean;
	disabled?: boolean;
	trailing?: React.ReactNode;
	onClick: () => void;
}) {
	const label = danger
		? "text-[#b91c1c]"
		: isActive
			? "text-[#0c63f8]"
			: "text-[#101828]";
	const iconTone = danger
		? "text-[#b91c1c]"
		: isActive
			? "text-[#0c63f8]"
			: "text-[#4a5565]";
	return (
		<button
			type="button"
			onClick={onClick}
			disabled={disabled}
			className={`flex w-full items-center gap-4 px-6 py-3 text-left active:bg-[#f9fafb] disabled:active:bg-transparent ${label} ${className ?? ""}`}
		>
			<Icon
				className={`size-[22px] shrink-0 ${iconTone} ${iconClassName ?? ""}`}
				strokeWidth={stroke}
			/>
			<span className="min-w-0 flex-1 truncate font-semibold text-base leading-6">
				{title}
			</span>
			{trailing}
		</button>
	);
}

/**
 * Figma `separator`: a hairline with 8px of air either side. Full-bleed above
 * Log Out; `inset` lines up with the 32px indent of the expanded settings rail,
 * the only other place Figma draws one.
 */
function SheetSeparator({ inset }: { inset?: boolean }) {
	return (
		<div className="py-2">
			<div className={`h-px bg-[#f3f4f6] ${inset ? "mx-8" : ""}`} />
		</div>
	);
}

/**
 * The "More" bottom sheet — the overflow half of the mobile navigation that
 * {@link MobileTabBar} opens. Matches the "15 More sheet" Figma screen.
 */
export function MobileFlexDrawer({
	open,
	onOpenChange,
	onNavigate,
	pathname,
	user,
	partnerConfig,
	onLogout,
}: MobileFlexDrawerProps) {
	const [settingsOpen, setSettingsOpen] = useState(false);
	const [isSigningOut, setIsSigningOut] = useState(false);

	const config = partnerConfig ?? {
		navbarConfig: [
			"dashboard",
			"leads",
			"commissions",
			"contracts",
			"listings",
			"white_label_listings",
			"sub-partners",
			"campaigns",
			"integration",
			"support",
			"white_label_settings",
		],
		isSubPartner: false,
		isAuthorized: true,
		isBookingPartner: false,
		isPartnerCustomFormEnabled: true,
	};

	const {
		navbarConfig,
		isSubPartner,
		isBookingPartner,
		isPartnerCustomFormEnabled,
		partnerName,
	} = config;

	const displayName = partnerName || user?.name || "Partner";
	const partnerEmail = user?.email || "";

	// A failed logout leaves the app where it was, so the next time the sheet
	// opens the row has to be a Log Out button again rather than a stuck spinner.
	const handleOpenChange = (next: boolean) => {
		if (!next) setIsSigningOut(false);
		onOpenChange(next);
	};

	const handleNavigation = (href: string, internal?: boolean) => {
		onNavigate(href, internal);
		handleOpenChange(false);
	};

	const navItems = useMemo(
		() => [
			{
				title: "Bookings",
				href: "/dashboard/bookings",
				icon: CalendarCheck,
				stroke: NAV_STROKE_LUCIDE,
				isActive: pathname.includes("/bookings"),
				isAccessible: !isBookingPartner && showNavItem("leads", navbarConfig),
			},
			{
				title: "Contracts",
				href: "/dashboard/contracts",
				icon: FileSignature,
				stroke: NAV_STROKE_LUCIDE,
				isActive: pathname.includes("/contracts"),
				isAccessible: true,
			},
			{
				title: "Sub-Partners",
				href: "/dashboard/subpartners",
				icon: SubPartnersIcon,
				stroke: NAV_STROKE_LOCAL,
				isActive: pathname.includes("/subpartners"),
				isAccessible:
					!isSubPartner && showNavItem("sub-partners", navbarConfig),
			},
			{
				title: "Campaigns / Events",
				href: "/dashboard/campaigns",
				icon: CampaignsIcon,
				stroke: NAV_STROKE_LOCAL,
				isActive: pathname.includes("/campaigns"),
				isAccessible: !isSubPartner && showNavItem("campaigns", navbarConfig),
			},
			{
				title: "Integrations",
				href: "/dashboard/integrations",
				icon: Link,
				stroke: NAV_STROKE_LUCIDE,
				isActive: pathname.includes("/integrations"),
				isAccessible: showNavItem("integration", navbarConfig),
			},
			{
				title: "Support",
				href: "/dashboard/support",
				icon: SupportIcon,
				stroke: NAV_STROKE_LOCAL,
				isActive: pathname.includes("/support"),
				isAccessible: showNavItem("support", navbarConfig),
			},
		],
		[pathname, navbarConfig, isSubPartner, isBookingPartner],
	);

	const settingsSubItems = useMemo(() => {
		const baseItems = [
			{
				title: "Site Configuration",
				href: "/dashboard/settings",
				isAccessible: showNavItem("white_label_settings", navbarConfig),
			},
		];

		if (isPartnerCustomFormEnabled) {
			baseItems.push({
				title: "Custom Form",
				href: "/dashboard/custom-form",
				isAccessible: showNavItem("white_label_settings", navbarConfig),
			});
		}

		baseItems.push({
			title: "Custom Page",
			href: "/dashboard/custom-page",
			isAccessible: showNavItem("white_label_settings", navbarConfig),
		});

		baseItems.push({
			title: "Whitelabel Partner Listings",
			href: "/dashboard/whitelabel-partner-listings",
			isAccessible: showNavItem("white_label_inventories", navbarConfig),
		});

		return baseItems.filter((item) => item.isAccessible);
	}, [navbarConfig, isPartnerCustomFormEnabled]);

	const showSettings =
		(showNavItem("white_label_settings", navbarConfig) ||
			showNavItem("white_label_inventories", navbarConfig)) &&
		settingsSubItems.length > 0;

	const isOnSettingsPage = useMemo(
		() =>
			settingsSubItems.some((item) => pathname.startsWith(`/flex${item.href}`)),
		[settingsSubItems, pathname],
	);

	const accessibleNavItems = navItems.filter((item) => item.isAccessible);

	return (
		<Sheet open={open} onOpenChange={handleOpenChange}>
			<SheetContent
				side="bottom"
				aria-label="More"
				// The sheet header already carries its own close button, and Figma
				// draws only that one.
				showCloseButton={false}
				// Figma `Bottom sheet · More` 2174:20270 — the sheet hugs its rows
				// (8px above the grab handle, 16px below the last one) and grows as
				// the settings rail opens, scrolling only once it runs out of screen.
				className="max-h-[88dvh] gap-0 overflow-y-auto rounded-t-[22px] bg-white px-0 pt-2 pb-4"
			>
				<SheetTitle className="sr-only">More</SheetTitle>

				{/* Grab handle */}
				<div className="flex justify-center">
					<div className="h-[5px] w-[38px] rounded-full bg-[#d1d5dc]" />
				</div>

				{/* Figma `Menu` — one flush stack; the sheet's only gap is the
				    4px between the grab handle and this. */}
				<div className="mt-1 flex flex-col">
					{/* Sheet header — partner identity */}
					<div className="flex items-center gap-3 border-[#e4e4e4] border-b px-5 pt-3 pb-4">
						<span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-[#dbeafe] font-semibold text-[#155dfc] text-base">
							{displayName.charAt(0).toUpperCase()}
						</span>
						<div className="flex min-w-0 flex-1 flex-col">
							<span className="truncate font-semibold text-[#0a0a0a] text-base leading-6">
								{displayName}
							</span>
							{partnerEmail && (
								<span className="truncate text-[#747474] text-xs leading-4">
									{partnerEmail}
								</span>
							)}
						</div>
						<CloseButton
							label="Close menu"
							onClick={() => handleOpenChange(false)}
						/>
					</div>

					{/* Section nav */}
					{accessibleNavItems.map((item, index) => (
						<SheetRow
							key={item.title}
							className={index === 0 ? "pt-4" : undefined}
							icon={item.icon}
							stroke={item.stroke}
							title={item.title}
							isActive={item.isActive}
							onClick={() => handleNavigation(item.href, true)}
						/>
					))}

					{/* Settings — expandable */}
					{showSettings && (
						<>
							<SheetRow
								icon={Settings}
								title="Settings"
								isActive={isOnSettingsPage}
								onClick={() => setSettingsOpen(!settingsOpen)}
								trailing={
									settingsOpen ? (
										<ChevronUp className="size-[18px] shrink-0 text-[#99a1af]" />
									) : (
										<ChevronDown className="size-[18px] shrink-0 text-[#99a1af]" />
									)
								}
							/>
							{settingsOpen && (
								<div className="pl-8">
									<div className="flex flex-col border-[#3b82f6] border-l-2">
										{settingsSubItems.map((subItem) => (
											<button
												type="button"
												key={subItem.title}
												onClick={() => handleNavigation(subItem.href, true)}
												className={`flex h-10 items-center pr-6 pl-7 text-left font-medium text-sm leading-[18px] ${
													pathname === `/flex${subItem.href}`
														? "text-[#0c63f8]"
														: "text-[#4b5563]"
												}`}
											>
												<span className="truncate">{subItem.title}</span>
											</button>
										))}
									</div>
								</div>
							)}
							{/* Figma 2174:20270 vs 2174:21678 — the rule under the rail is
							    part of the expanded settings block: the collapsed sheet
							    runs Settings straight into Account Details. */}
							{settingsOpen && <SheetSeparator inset />}
						</>
					)}

					{/* Account */}
					<SheetRow
						icon={CircleUser}
						title="Account Details"
						isActive={pathname.includes("/profile")}
						onClick={() => handleNavigation("/dashboard/profile", true)}
					/>
					<SheetRow
						icon={Users}
						title="Teams"
						isActive={pathname.includes("/teams")}
						onClick={() => handleNavigation("/dashboard/teams", true)}
					/>
					<SheetRow
						icon={ExternalLink}
						title="Visit amber student"
						onClick={() => {
							// Figma `34 Visit amber student — External hand-off`: the sheet
							// closes and a toast says where the tap went, since the new tab
							// may open behind the current one.
							handleOpenChange(false);
							toast("Opening amberstudent.com in a new tab", {
								icon: <ExternalLink className="size-[18px]" />,
							});
							safeOpen("https://amberstudent.com");
						}}
					/>

					<SheetSeparator />

					{/* Figma `33 Log Out — Signing out`: the sheet stays put and this row
					    becomes the progress indicator, rather than the menu vanishing
					    before anything visibly happens. */}
					<SheetRow
						icon={isSigningOut ? Loader2 : LogOut}
						iconClassName={isSigningOut ? "animate-spin" : undefined}
						title={isSigningOut ? "Signing you out\u2026" : "Log Out"}
						danger
						disabled={isSigningOut}
						onClick={() => {
							setIsSigningOut(true);
							onLogout?.();
						}}
					/>
				</div>
			</SheetContent>
		</Sheet>
	);
}
